import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from "../context/ThemeContext";
import {
  readKpiCalculationSyncState,
  runSharedKpiCalculations,
  subscribeToKpiCalculationSync,
} from '../utils/kpiCalculationSync';

type GeneratedSqlEntry = {
  sql?: string;
  strategy?: unknown;
  diagnostics?: {
    used_llm?: boolean;
    fallback_used?: boolean;
    repaired?: boolean;
    repair_attempts?: number;
    fallback_reason?: string;
    validation_error?: string;
  };
};

declare global {
  interface Window {
    __generateAllCancel?: () => void;
  }
}

let API = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const KPI_API_URL =
  `${API}/api/kpi/user-scoped`;
const KPI = 
  `${API}/api/kpi`;
const NEWLY_ADDED_KPI_IDS_KEY = "newlyAddedKpiIds";

function getCurrentUserId() {
  const directUserId = String(localStorage.getItem('userId') || '').trim();
  if (directUserId) return directUserId;

  try {
    const rawUser = localStorage.getItem('authUser');
    if (!rawUser) return '';
    const parsedUser = JSON.parse(rawUser);
    const candidate = parsedUser?.id ?? parsedUser?.user_id ?? parsedUser?.userId;
    return candidate == null ? '' : String(candidate).trim();
  } catch {
    return '';
  }
}

function buildApiCandidates(path) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const candidates = [
    API + normalizedPath,
  ].filter(Boolean);

  return [...new Set(candidates)];
}

async function fetchWithTimeout(url, options = {}, timeoutMs = 20000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal, credentials: 'include' });
  } finally {
    clearTimeout(timer);
  }
}

async function requestWithFallback(path, options = {}, timeoutMs = 20000) {
  const urls = buildApiCandidates(path);
  let lastError = null;

  for (const url of urls) {
    try {
      const response = await fetchWithTimeout(url, options, timeoutMs);
      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      const isHtmlResponse = contentType.includes('text/html');

      if (response.ok && !isHtmlResponse) return response;

      if (response.ok && isHtmlResponse) {
        lastError = new Error(`Received HTML instead of API response from ${url}`);
        continue;
      }

      lastError = new Error(`Request failed (${response.status})`);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error('Request failed');
}

async function requestGenerateSql(rows, validateSql = true, timeoutMs = 120000) {
  const payload = { rows, validate_sql: validateSql };
  try {
    const strategyResp = await requestWithFallback('/api/kpi/generate_sql_strategy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }, timeoutMs);
    return strategyResp;
  } catch (_) {
    return requestWithFallback('/api/kpi/generate_sql', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }, timeoutMs);
  }
}

function toHeader(str) {
  return String(str || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function pickValue(row, keys) {
  const lowerMap = Object.fromEntries(Object.entries(row || {}).map(([k, v]) => [String(k).toLowerCase(), v]));
  for (const key of keys) {
    const value = lowerMap[String(key).toLowerCase()];
    if (value !== undefined && value !== null && String(value).trim() !== '') return value;
  }
  return '';
}

function formatCalcCellValue(value, columnName = '') {
  if (value === null || value === undefined || value === '') return '';

  const column = String(columnName || '').toLowerCase();
  const shouldRound = column === 'value' || column === 'kpi_value' || column === 'calculated_value';

  if (!shouldRound) return String(value);

  if (typeof value === 'number' && Number.isFinite(value)) return value.toFixed(2);

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed !== '' && /^-?\d+(\.\d+)?$/.test(trimmed)) {
      const parsed = Number(trimmed);
      if (Number.isFinite(parsed)) return parsed.toFixed(2);
    }
  }

  return String(value);
}

export default function KpiUpload() {
  const getInitialNewlyAddedKpiIds = () => {
    try {
      const raw = localStorage.getItem(NEWLY_ADDED_KPI_IDS_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set();
      return new Set(parsed.map((id) => Number(id)).filter((id) => Number.isFinite(id)));
    } catch {
      return new Set();
    }
  };

  const [rows, setRows] = useState([]);
  const [cols, setCols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addKpiLoading, setAddKpiLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [savingIds, setSavingIds] = useState({});
  const [genResultMap, setGenResultMap] = useState<Record<string, GeneratedSqlEntry>>({});
  const [editMap, setEditMap] = useState({});
  const [rowDraftSnapshots, setRowDraftSnapshots] = useState({});
  const [showGenerateAllModal, setShowGenerateAllModal] = useState(false);
  const [generateAllProgress, setGenerateAllProgress] = useState({ done: 0, total: 0, current: '', failures: [] });
  const [generateAllCancelled, setGenerateAllCancelled] = useState(false);
  const [showCalculationModal, setShowCalculationModal] = useState(false);
  const [showCalcResults, setShowCalcResults] = useState(false);
  const [showAddKpiModal, setShowAddKpiModal] = useState(false);
  const [newKpiName, setNewKpiName] = useState('');
  const [newKpiDescription, setNewKpiDescription] = useState('');
  const [newKpiSource, setNewKpiSource] = useState('');
  const [newKpiCategory, setNewKpiCategory] = useState('');
  const [newKpiFunction, setNewKpiFunction] = useState('');
  const [newlyAddedKpiIds, setNewlyAddedKpiIds] = useState(getInitialNewlyAddedKpiIds);
  const [addKpiApiMessage, setAddKpiApiMessage] = useState('');
  const [showErrorOnAddKpi, setShowErrorOnAddKpi] = useState(false);
  const [KpiData, setKpiData] = useState([]);
  const [kpiCategories, setKpiCategories] = useState([]);
  const [kpiFunctions, setKpiFunctions] = useState([]);
  const [kpiSources, setKpiSources] = useState([]);

  const [companyOptions, setCompanyOptions] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState('__ALL__');
  const [calculationProgress, setCalculationProgress] = useState({
    companiesDone: 0,
    companiesTotal: 0,
    kpisDone: 0,
    kpisTotal: 0,
  });
  const { isDarkMode } = useTheme();
  const [calcCurrentCompany, setCalcCurrentCompany] = useState('');
  const [calcFailures, setCalcFailures] = useState([]);
  const [calcResultsCompanyId, setCalcResultsCompanyId] = useState('');
  const [calcResultsData, setCalcResultsData] = useState([]);
  const [loadingCalcResults, setLoadingCalcResults] = useState(false);
  const [kpiSelectionMode, setKpiSelectionMode] = useState('all');
  const [selectedKpiIds, setSelectedKpiIds] = useState(new Set());
  const [kpiSearchQuery, setKpiSearchQuery] = useState('');

  useEffect(() => {
    try {
      localStorage.setItem(
        NEWLY_ADDED_KPI_IDS_KEY,
        JSON.stringify(Array.from(newlyAddedKpiIds)),
      );
    } catch {
      // ignore storage errors
    }
  }, [newlyAddedKpiIds]);

  // Fetch KPI categories for Add KPI modal dropdown
  useEffect(() => {
    let isMounted = true;
    fetch(KPI, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        if (!isMounted) return;
        const kpis = data.kpis || [];
        const cats = [...new Set(kpis.map((k) => k.KPI_Category).filter(Boolean))];
        const functions = [...new Set(kpis.map((k) => k.Function).filter(Boolean))];
        setKpiCategories(cats);
        setKpiFunctions(functions);
      })
      .catch(() => {});
    return () => { isMounted = false; };
  }, []);

  // Fetch KPI sources for Add KPI modal dropdown
  useEffect(() => {
    let isMounted = true;
    fetch(`${API}/api/kpi/sources`, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        if (!isMounted) return;
        setKpiSources(data.sources || []);
      })
      .catch(() => {});
    return () => { isMounted = false; };
  }, []);

  // Generate SQL selection modal states
  const [showGenSqlSelectModal, setShowGenSqlSelectModal] = useState(false);
  const [genSqlKpiMode, setGenSqlKpiMode] = useState('all');
  const [genSqlSelectedIds, setGenSqlSelectedIds] = useState(new Set());
  const [genSqlSearchQuery, setGenSqlSearchQuery] = useState('');

  useEffect(() => {
    const applySyncState = (state) => {
      if (!state) return;

      setCalculating(Boolean(state.calculating));
      setCalcCurrentCompany(state.currentCompany || '');
      setCalcFailures(Array.isArray(state.failures) ? state.failures : []);

      if (state.progress) {
        setCalculationProgress((prev) => ({
          ...prev,
          ...state.progress,
        }));
      }
    };

    applySyncState(readKpiCalculationSyncState());
    return subscribeToKpiCalculationSync(applySyncState);
  }, []);

  const visibleKpis = useMemo(() => {
    const q = kpiSearchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const id = String(row.id || '').toLowerCase();
      const name = String(pickValue(row, ['KPI_FORMULA', 'kpi_formula', 'kpi_name', 'KPI_Name', 'name']) || '').toLowerCase();
      return id.includes(q) || name.includes(q);
    });
  }, [rows, kpiSearchQuery]);

  const selectedCalculationRows = useMemo(() => {
    if (kpiSelectionMode !== 'selected') return rows;
    return rows.filter((row) => selectedKpiIds.has(Number(row.id)));
  }, [rows, kpiSelectionMode, selectedKpiIds]);

  const eligibleCalculationRows = useMemo(() => {
    return selectedCalculationRows.filter((row) => String(row.sql_equivalent || '').trim());
  }, [selectedCalculationRows]);

  const genSqlVisibleKpis = useMemo(() => {
    const q = genSqlSearchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const id = String(row.id || '').toLowerCase();
      const name = String(pickValue(row, ['KPI_FORMULA', 'kpi_formula', 'kpi_name', 'KPI_Name', 'name']) || '').toLowerCase();
      return id.includes(q) || name.includes(q);
    });
  }, [rows, genSqlSearchQuery]);

  const genSqlTargetRows = useMemo(() => {
    if (genSqlKpiMode !== 'selected') return rows;
    return rows.filter((row) => genSqlSelectedIds.has(Number(row.id)));
  }, [rows, genSqlKpiMode, genSqlSelectedIds]);

  const calculationTargets = useMemo(() => {
    const companiesTotal = selectedCompanyId === '__ALL__'
      ? companyOptions.length
      : selectedCompanyId && selectedCompanyId !== '__ALL__'
        ? 1
        : 0;

    const selectedKpisTotal = selectedCalculationRows.length;
    const eligibleKpisTotal = eligibleCalculationRows.length;

    return {
      companiesTotal,
      selectedKpisTotal,
      eligibleKpisTotal,
      kpiRunsTotal: companiesTotal * eligibleKpisTotal,
    };
  }, [selectedCompanyId, companyOptions.length, selectedCalculationRows, eligibleCalculationRows]);

  const companyProgressPct = calculationProgress.companiesTotal
    ? Math.min(100, Math.round((calculationProgress.companiesDone / calculationProgress.companiesTotal) * 100))
    : 0;

  const kpiProgressPct = calculationProgress.kpisTotal
    ? Math.min(100, Math.round((calculationProgress.kpisDone / calculationProgress.kpisTotal) * 100))
    : 0;

  useEffect(() => {
    loadKpis();
  }, []);

  async function loadKpis() {
    setLoading(true);
    try {
      const resp = await requestWithFallback('/api/kpi/user-scoped?include_sql_equivalent=true');
      const data = await resp.json();
      const kpis = (data.allowed_kpis || []).map((row) => {
        const next = { ...row };

        const normalizedSource = pickValue(next, ['source', 'Source', 'Source_Census_P_L', 'source_census_p_l']);
        if (normalizedSource !== '') next.source = normalizedSource;

        delete next.Source;
        delete next.Source_Census_P_L;
        delete next.source_census_p_l;

        return next;
      });

      const currentUserId = getCurrentUserId();
      const existingRows = kpis.filter((row) => {
        const rowUserId = row?.user_id;
        return rowUserId == null || String(rowUserId).trim() === '';
      });

      const currentUserRows = currentUserId
        ? kpis.filter((row) => {
            const rowUserId = row?.user_id;
            if (rowUserId == null || String(rowUserId).trim() === '') return false;
            return String(rowUserId).trim() === currentUserId;
          })
        : [];

      const visibleRows = [...existingRows, ...currentUserRows];
      setRows(visibleRows);

      const preferred = ['id', 'KPI_FORMULA', 'kpi_formula', 'kpi_name', 'KPI_Name'];
      const all = Array.from(new Set(visibleRows.flatMap((row) => Object.keys(row || {}))));
      const ordered = [
        ...preferred.filter((key) => all.includes(key)),
        ...all.filter(
          (key) => !preferred.includes(key)
            && key !== 'sql_equivalent'
            && key !== 'created_at'
            && key !== 'createdAt'
            && key !== 'source'
            && key !== 'Source'
            && key !== 'Source_Census_P_L'
            && key !== 'source_census_p_l'
            && key !== 'Agent'
            && key !== 'user_id'
        ),
        // ...(all.includes('sql_equivalent') ? ['sql_equivalent'] : []),
      ];
      console.log(visibleRows, "all columns");
      setCols(ordered);
      setEditMap({});
      setRowDraftSnapshots({});
    } catch (e) {
      alert(`Failed to load KPIs: ${e.message}`);
      setRows([]);
      setCols([]);
    } finally {
      setLoading(false);
    }
  }

  async function onFile(e) {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    const fd = new FormData();
    fd.append('file', selectedFile);
    setUploading(true);
    try {
      await requestWithFallback('/api/kpi/upload', { method: 'POST', body: fd });
      await loadKpis();
      alert('KPI file uploaded successfully');
    } catch (err) {
      alert(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  function openAddKpiModal() {
    setNewKpiName('');
    setNewKpiSource('');
    setNewKpiCategory('');
    setAddKpiApiMessage('');
    setShowAddKpiModal(true);
    setShowErrorOnAddKpi(false);
    setAddKpiLoading(false);
    setNewKpiFunction('');
  }

   async function submitNewKpi() {
    const trimmedName = newKpiName.trim();
//     const trimmedSource = newKpiSource.trim();
    const trimmedCategory = newKpiCategory.trim();
    const trimmedFunction = newKpiFunction.trim();
    const trimmedDescription = newKpiDescription.trim();
  setAddKpiApiMessage('');
  setAddKpiLoading(true);
    if (!trimmedName || !trimmedDescription|| !trimmedCategory || !trimmedFunction) {
      setShowErrorOnAddKpi(true);
      setShowAddKpiModal(true);
      setAddKpiLoading(false);
      return;
    }

    try {
      const resp = await requestWithFallback('/api/kpi/add-generated', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          kpi_name: trimmedName,
          description: trimmedDescription,
          kpi_category: trimmedCategory,
          function: trimmedFunction,
        }),
      });
      const data = await resp.json();

      if (data?.status === 'exists') {
        setAddKpiApiMessage('KPI already exists');
        setAddKpiLoading(false);
        return;
      }
 if (data?.status === 'success') {
        setAddKpiApiMessage('New KPI added successfully');
        setAddKpiLoading(false);
        await loadKpis();
        setTimeout(() => {
          setShowAddKpiModal(false);
          setAddKpiApiMessage('');
        }, 1500);
      }

      // Reset form fields
      setNewKpiName('');
      setNewKpiDescription('');
      setNewKpiCategory('');
      setShowErrorOnAddKpi(false);
      setShowAddKpiModal(false);
      setNewKpiFunction('');
      

      const insertedId = Number(data?.inserted_id);
      if (Number.isFinite(insertedId)) {
        setNewlyAddedKpiIds((prev) => new Set([...prev, insertedId]));
        await runCalculations([insertedId]);
      }
    } catch (err) {
      console.error('Error adding KPI:', err);
    } finally {
      setAddKpiLoading(false);
    }

  }

  function onCellChange(index, key, value) {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  }

  function toggleEdit(index) {
    setEditMap((prev) => ({ ...prev, [index]: true }));
    const snapshot = rows[index] ? { ...rows[index] } : null;
    if (!snapshot) return;
    setRowDraftSnapshots((prev) => {
      return { ...prev, [index]: snapshot };
    });
  }

  function cancelEdit(index) {
    setRows((prevRows) => {
      const snapshot = rowDraftSnapshots[index];
      if (!snapshot) return prevRows;
      return prevRows.map((row, i) => (i === index ? { ...snapshot } : row));
    });

    setEditMap((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });

    setRowDraftSnapshots((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }

  async function saveRow(index) {
    const row = rows[index];
    if (!row) return;
    setSavingIds((prev) => ({ ...prev, [index]: true }));
    try {
      await requestWithFallback('/api/kpi/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: [row] }),
      });
      await loadKpis();
      setEditMap((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      setRowDraftSnapshots((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    } catch (e) {
      alert(`Save failed: ${e.message}`);
    } finally {
      setSavingIds((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  }

  async function deleteRow(index) {
    const row = rows[index];
    if (!row?.id) {
      alert('Cannot delete unsaved row');
      return;
    }
    if (!window.confirm('Delete this KPI row? This cannot be undone.')) return;
    try {
      await requestWithFallback('/api/kpi/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [row.id] }),
      });
      setNewlyAddedKpiIds((prev) => {
        const next = new Set(prev);
        next.delete(Number(row.id));
        return next;
      });
      await loadKpis();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    }
  }

  async function generateSqlForRow(row) {
    try {
      const payloadRow = {
        id: row.id,
        KPI_FORMULA: pickValue(row, ['KPI_FORMULA', 'kpi_formula', 'kpi_name', 'KPI_Name', 'name']),
        SOURCE: pickValue(row, ['Source', 'source', 'Source_Census_P_L', 'source_census_p_l']),
      };
      const resp = await requestGenerateSql([payloadRow], true);
      const j = await resp.json();
      const sql = j.results?.[0]?.sql || '';
      const strategy = j.results?.[0]?.strategy || {};
      const diagnostics = j.results?.[0]?.diagnostics || {};
      const key = row.id || pickValue(row, ['KPI_FORMULA', 'kpi_formula', 'kpi_name', 'KPI_Name', 'name']);
      setGenResultMap((prev) => ({ ...prev, [key]: { sql, strategy, diagnostics } }));
    } catch (e) {
      alert(`Generation error: ${e.message}`);
    }
  }

  async function approveSqlForRow(row) {
    const key = row.id || pickValue(row, ['KPI_FORMULA', 'kpi_formula', 'kpi_name', 'KPI_Name', 'name']);
    const sql = genResultMap[key]?.sql;
    if (!sql || !row?.id) return;
    try {
      await requestWithFallback('/api/kpi/update_sql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: [{ id: row.id, sql_equivalent: sql }] }),
      });
      setGenResultMap((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      await loadKpis();
    } catch (e) {
      alert(`Approve SQL failed: ${e.message}`);
    }
  }

  function cancelSql(row) {
    const key = row.id || pickValue(row, ['KPI_FORMULA', 'kpi_formula', 'kpi_name', 'KPI_Name', 'name']);
    setGenResultMap((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function openGenSqlSelectModal() {
    setGenSqlKpiMode('all');
    setGenSqlSelectedIds(new Set());
    setGenSqlSearchQuery('');
    setShowGenSqlSelectModal(true);
  }

  function startGenerateSql() {
    setShowGenSqlSelectModal(false);
    const targetRows = genSqlKpiMode === 'selected' ? genSqlTargetRows : rows;
    generateSqlForAll(targetRows);
  }

  async function generateSqlForAll(targetRows) {
    if (!targetRows || !targetRows.length) return;
    setGeneratingAll(true);
    setGenerateAllCancelled(false);
    setShowGenerateAllModal(true);

    const total = targetRows.length;
    const failures = [];
    setGenerateAllProgress({ done: 0, total, current: '', failures: [] });

    let cancelled = false;
    // Store cancel ref so inner loop can check
    const cancelRef = { current: false };
    setGenerateAllCancelled(false);
    // We'll expose a setter that also stores to ref
    const markCancelled = () => { cancelRef.current = true; setGenerateAllCancelled(true); };
    // Expose on window briefly so the modal cancel button can call it
    window.__generateAllCancel = markCancelled;

    for (let i = 0; i < total; i++) {
      if (cancelRef.current) break;

      const row = targetRows[i];
      const kpiName = String(
        pickValue(row, ['KPI_FORMULA', 'kpi_formula', 'kpi_name', 'KPI_Name', 'name']) || `KPI #${row.id || i + 1}`,
      );
      setGenerateAllProgress((prev) => ({ ...prev, current: kpiName }));

      try {
        const payloadRow = {
          id: row.id,
          KPI_FORMULA: kpiName,
          SOURCE: pickValue(row, ['Source', 'source', 'Source_Census_P_L', 'source_census_p_l']),
        };
        const resp = await requestGenerateSql([payloadRow], true, 120000);
        const j = await resp.json();
        const sql = j.results?.[0]?.sql || '';
        const strategy = j.results?.[0]?.strategy || {};
        const diagnostics = j.results?.[0]?.diagnostics || {};
        const key = row.id || kpiName;

        // Store in genResultMap so badges appear in the table
        setGenResultMap((prev) => ({ ...prev, [key]: { sql, strategy, diagnostics } }));

        // Auto-commit: save the generated SQL immediately
        if (row.id && sql) {
          try {
            await requestWithFallback('/api/kpi/update_sql', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ rows: [{ id: row.id, sql_equivalent: sql }] }),
            });
          } catch (saveErr) {
            failures.push({ kpi: kpiName, error: `Save failed: ${saveErr.message}` });
          }
        }
      } catch (e) {
        failures.push({ kpi: kpiName, error: e.message });
      }

      setGenerateAllProgress((prev) => ({ ...prev, done: i + 1, failures: [...failures] }));
    }

    delete window.__generateAllCancel;

    // Clear generated-result badges since everything was already auto-committed
    setGenResultMap({});

    // Refresh table data so saved SQL is reflected
    await loadKpis();

    setGenerateAllProgress((prev) => ({
      ...prev,
      done: cancelRef.current ? prev.done : total,
      current: cancelRef.current ? 'Cancelled' : 'Complete',
      failures: [...failures],
    }));

    setGeneratingAll(false);
    // Keep modal open so user can see final status; they close it manually
  }

  const pendingApprovalCount = useMemo(() => {
    return Object.entries(genResultMap).filter(([, v]) => v?.sql).length;
  }, [genResultMap]);

  async function approveAllGenerated() {
    const rowsToApprove = [];
    for (const row of rows) {
      const key = row.id || pickValue(row, ['KPI_FORMULA', 'kpi_formula', 'kpi_name', 'KPI_Name', 'name']);
      const entry = genResultMap[key];
      if (entry?.sql && row.id) {
        rowsToApprove.push({ id: row.id, sql_equivalent: entry.sql });
      }
    }
    if (!rowsToApprove.length) return;
    try {
      await requestWithFallback('/api/kpi/update_sql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: rowsToApprove }),
      });
      setGenResultMap({});
      await loadKpis();
      alert(`Approved SQL for ${rowsToApprove.length} KPI(s)`);
    } catch (e) {
      alert(`Approve all failed: ${e.message}`);
    }
  }

  async function downloadKpis() {
    setDownloading(true);
    try {
      const resp = await requestWithFallback('/api/kpi/download');
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'kpi_list.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Download failed: ${e.message}`);
    } finally {
      setDownloading(false);
    }
  }

  async function openCalculationModal() {
    setShowCalculationModal(true);
    setKpiSelectionMode('all');
    setSelectedKpiIds(new Set());
    setKpiSearchQuery('');
    if (companyOptions.length) return;
    setLoadingCompanies(true);
    try {
      const resp = await requestWithFallback('/api/kpi/companies');
      const j = await resp.json();
      setCompanyOptions(j.companies || []);
    } catch (e) {
      alert(e.message);
    } finally {
      setLoadingCompanies(false);
    }
  }

  async function runCalculations(explicitKpiIds = null) {
    const hasExplicitKpiIds = Array.isArray(explicitKpiIds) && explicitKpiIds.length > 0;

    if (!hasExplicitKpiIds && !eligibleCalculationRows.length) {
      alert('No approved SQL found for the selected KPI set. Generate and approve SQL before running calculations.');
      return;
    }

    const manualCompanyIds =
      selectedCompanyId === '__ALL__'
        ? null
        : [Number(selectedCompanyId)].filter((id) => Number.isFinite(id));

    const manualKpiIds = hasExplicitKpiIds
      ? explicitKpiIds.map(Number).filter((id) => Number.isFinite(id))
      : kpiSelectionMode === 'selected' && selectedKpiIds.size > 0
        ? Array.from(selectedKpiIds).map(Number).filter((id) => Number.isFinite(id))
        : eligibleCalculationRows
            .map((row) => Number(row.id))
            .filter((id) => Number.isFinite(id));

    try {
      await runSharedKpiCalculations({
        companyIds: manualCompanyIds,
        kpiIds: manualKpiIds,
      });
    } catch (e) {
      alert(`Run calculations failed: ${e.message}`);
    }
  }

  async function openCalcResultsModal() {
    setShowCalcResults(true);
    if (!companyOptions.length) {
      setLoadingCompanies(true);
      try {
        const resp = await requestWithFallback('/api/kpi/companies');
        const j = await resp.json();
        setCompanyOptions(j.companies || []);
        if (!calcResultsCompanyId && j.companies?.[0]?.id) {
          setCalcResultsCompanyId(String(j.companies[0].id));
        }
      } finally {
        setLoadingCompanies(false);
      }
    }
  }

  async function loadCalcResults(companyId) {
    if (!companyId) return;
    setLoadingCalcResults(true);
    try {
      const resp = await requestWithFallback(`/api/kpi/calculated_values?company_id=${encodeURIComponent(companyId)}`);
      const j = await resp.json();
      setCalcResultsData(j.rows || []);
    } catch (e) {
      alert(e.message);
      setCalcResultsData([]);
    } finally {
      setLoadingCalcResults(false);
    }
  }

  useEffect(() => {
    if (showCalcResults && calcResultsCompanyId) loadCalcResults(calcResultsCompanyId);
  }, [showCalcResults, calcResultsCompanyId]);

  const canRunSelected = kpiSelectionMode === 'all' || selectedKpiIds.size > 0;

  return (
    <div className={`${
          isDarkMode ? "bg-black" : "bg-[#f4fbf6]"
        } min-h-screen w-full relative overflow-hidden`}>
      {!isDarkMode && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-16 -left-10 h-56 w-56 rounded-full bg-emerald-100/70 blur-3xl"></div>
          <div className="absolute top-20 right-10 h-48 w-48 rounded-full bg-sky-100/70 blur-3xl"></div>
          <div className="absolute bottom-8 left-1/3 h-44 w-44 rounded-full bg-indigo-100/60 blur-3xl"></div>
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-8 pb-10">
        <div className="text-center pb-6">
          <span className="inline-flex items-center rounded-full border border-emerald-300/80 bg-emerald-50 px-4 py-1 text-xs font-bold uppercase tracking-wide text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
            KPI Management
          </span>
          <h2 className="mt-3 text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white">Manage KPIs</h2>
          <p className="mt-2 text-sm md:text-base text-gray-600 dark:text-gray-300">View and manage Benchmark KPIs.</p>
        </div>

      <div className="bg-white/95 dark:bg-gray-950/95 border border-emerald-100 dark:border-gray-800 rounded-2xl shadow-[0_14px_40px_rgba(16,24,40,0.08)] p-6 md:p-7 backdrop-blur">

        <div className="mb-4 flex flex-wrap gap-1.5 items-center justify-end">
            {/* <button title='Add New KPI' className="w-9 h-9 inline-flex items-center justify-center rounded-lg bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-60 transition-colors" onClick={openAddKpiModal} disabled={generatingAll}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14m-7-7h14" /></svg>
          </button> */}
          <label title="Upload Bulk KPI Excel" className={`relative inline-flex items-center justify-center w-9 h-9 rounded-lg bg-primary text-white transition-colors ${uploading ? 'opacity-60 cursor-not-allowed pointer-events-none animate-pulse' : 'cursor-pointer hover:opacity-80'}`}>
            <input type="file" accept=".xlsx,.xls" onChange={onFile} className="hidden" disabled={uploading} />
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 14l5-5m0 0l5 5m-5-5v12" /></svg>
          </label>
          {/* <button title={generatingAll ? 'Generating...' : 'Generate SQL'} className="w-9 h-9 inline-flex items-center justify-center rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 transition-colors" onClick={openGenSqlSelectModal} disabled={generatingAll}>
            {generatingAll
              ? <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
              : <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18l-1.813-2.096L5 15l2.187-.904L9 12l.813 2.096L12 15l-2.187.904zM16 4l.88 2.12L19 7l-2.12.88L16 10l-.88-2.12L13 7l2.12-.88L16 4zM18 13l1.061 2.559L21.62 16.62l-2.559 1.061L18 20.24l-1.061-2.559-2.559-1.061 2.559-1.061L18 13z" /></svg>
            }
          </button> */}
          {pendingApprovalCount > 0 && (
            <button title={`Approve All (${pendingApprovalCount})`} className="relative w-9 h-9 inline-flex items-center justify-center rounded-lg bg-yellow-600 text-white hover:bg-yellow-700 transition-colors" onClick={approveAllGenerated}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" /><path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">{pendingApprovalCount}</span>
            </button>
          )}
          <button title={downloading ? 'Downloading...' : 'Download KPIs'} className="w-9 h-9 inline-flex items-center justify-center rounded-lg bg-gray-700 text-white hover:bg-gray-800 disabled:opacity-60 transition-colors" onClick={downloadKpis} disabled={downloading}>
            {downloading
              ? <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
              : <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" /></svg>
            }
          </button>
          {localStorage.getItem("user_role") === "admin" && (
            <>
          <button title={calculating ? 'Calculating...' : 'Run Calculations'} className="w-9 h-9 inline-flex items-center justify-center rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60 transition-colors" onClick={openCalculationModal} disabled={calculating}>
            {calculating
              ? <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
              : <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-5.197-3.03A1 1 0 008 9v6a1 1 0 001.555.832l5.197-3.03a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" d="M4 5a2 2 0 012-2h12a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V5z" /></svg>
            }
          </button>
          <button title={loadingCalcResults ? 'Loading...' : 'Show Calculations'} className="w-9 h-9 inline-flex items-center justify-center rounded-lg bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-60 transition-colors" onClick={openCalcResultsModal} disabled={loadingCalcResults}>
            {loadingCalcResults
              ? <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
              : <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h5M5 6h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" /></svg>
            }
          </button>
          </>
          )}
        </div>

        <div className="overflow-auto border border-emerald-100 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 p-2 mt-4">
          {loading ? (
            <div className="w-full flex items-center justify-center py-16">
              <svg className="animate-spin h-8 w-8 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-emerald-50 dark:bg-emerald-950/30 border-b border-emerald-100 dark:border-gray-700">
                <tr>
                  {cols.map((c) => <th key={c} className="p-2 text-left whitespace-nowrap text-emerald-800 dark:text-emerald-200 font-semibold text-xs">{c === "id" ? "S.No" : toHeader(c)}</th>)}
                  <th className="p-2 text-left text-emerald-800 dark:text-emerald-200 font-semibold text-xs">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const rowKey = row.id || pickValue(row, ['KPI_FORMULA', 'kpi_formula', 'kpi_name', 'KPI_Name', 'name']) || index;
                  const generatedSql = genResultMap[rowKey]?.sql;
                  const diag: NonNullable<GeneratedSqlEntry['diagnostics']> = genResultMap[rowKey]?.diagnostics || {};
                  const currentUserId = String(localStorage.getItem("userId") || "").trim();
                  const rowUserId = row?.user_id == null ? '' : String(row.user_id).trim();
                  const isNewlyAddedRow = Boolean(currentUserId && rowUserId && rowUserId === currentUserId);
                  return (
                    <React.Fragment key={rowKey}>
                      <tr className="border-t border-gray-100 dark:border-gray-800 align-top hover:bg-emerald-50/30 dark:hover:bg-emerald-950/10 transition-colors">
                        {cols.map((col) => (
                          <td key={col} className="p-2">
                            {editMap[index] && col !== 'id' ? (
                              <input
                                className="w-full border rounded px-2 py-1 text-sm bg-white dark:bg-gray-900"
                                value={row[col] ?? ''}
                                onChange={(e) => onCellChange(index, col, e.target.value)}
                              />
                            ) : col === 'sql_equivalent' ? (
                              <div className="max-w-md whitespace-pre-wrap break-words text-xs text-gray-700 dark:text-gray-200">{String(row[col] || '')}</div>
                            ) : (
                              <span className="text-sm text-gray-800 dark:text-gray-200">{col === 'id' ? `${index+1}` : `${String(row[col] || '')}`}</span>
                            )}
                          </td>
                        ))}
                        <td className="p-2">
                          <div className="flex items-start gap-2">
                            {editMap[index] ? (
                              <>
                                <button title="Save" onClick={() => saveRow(index)} disabled={!!savingIds[index]} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" /><path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </button>
                                <button title="Cancel" onClick={() => cancelEdit(index)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l2 2m-2-2l-2-2" /><path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </button>
                              </>
                            ) : generatedSql ? (
                              <>
                                <button title="Approve SQL" onClick={() => approveSqlForRow(row)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" /><path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </button>
                                <button title="Cancel" onClick={() => cancelSql(row)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l2 2m-2-2l-2-2" /><path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  title={isNewlyAddedRow ? "Edit" : "Edit enabled only for newly added KPIs"}
                                  onClick={() => toggleEdit(index)}
                                  disabled={!isNewlyAddedRow}
                                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700 dark:text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487a2.25 2.25 0 113.182 3.182L9.75 17.963 6 18.75l.787-3.75L16.862 4.487z" /></svg>
                                </button>
                                {/* <button title="Generate SQL" onClick={() => generateSqlForRow(row)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18l-1.813-2.096L5 15l2.187-.904L9 12l.813 2.096L12 15l-2.187.904zM16 4l.88 2.12L19 7l-2.12.88L16 10l-.88-2.12L13 7l2.12-.88L16 4zM18 13l1.061 2.559L21.62 16.62l-2.559 1.061L18 20.24l-1.061-2.559-2.559-1.061 2.559-1.061L18 13z" /></svg>
                                </button> */}
                                <button
                                  title={isNewlyAddedRow ? "Delete" : "Delete enabled only for newly added KPIs"}
                                  onClick={() => deleteRow(index)}
                                  disabled={!isNewlyAddedRow}
                                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7" /><path strokeLinecap="round" strokeLinejoin="round" d="M10 11v6M14 11v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3M4 7h16" /></svg>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {generatedSql ? (
                        <tr className="border-b bg-gray-50/60 dark:bg-gray-800/30">
                          <td colSpan={cols.length + 1} className="p-2">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Generated SQL</span>
                              {diag.used_llm && !diag.fallback_used && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">LLM</span>
                              )}
                              {diag.repaired && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" title={`Repair attempts: ${diag.repair_attempts || 0}`}>Repaired ({diag.repair_attempts || 0}x)</span>
                              )}
                              {diag.fallback_used && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" title={diag.fallback_reason || ''}>Fallback</span>
                              )}
                              {diag.validation_error && !diag.fallback_used && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200" title={diag.validation_error}>Validation Warning</span>
                              )}
                            </div>
                            {diag.fallback_used && diag.fallback_reason && (
                              <div className="text-[11px] text-red-600 dark:text-red-400 mb-1">Reason: {diag.fallback_reason}</div>
                            )}
                            <pre className="p-2 text-xs rounded bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words overflow-auto max-h-48">{generatedSql}</pre>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      </div>

      {showCalculationModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm px-4 py-8 overflow-auto">
          <div className="w-full max-w-xl rounded-2xl bg-white dark:bg-gray-950 border border-emerald-100 dark:border-gray-700 shadow-[0_20px_60px_rgba(0,0,0,0.15)] p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Run KPI Calculations</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Choose a company and which KPIs to calculate.</p>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Company</label>
              <select className="w-full border rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900" value={selectedCompanyId} onChange={(e) => setSelectedCompanyId(e.target.value)} disabled={loadingCompanies || calculating}>
                <option value="__ALL__">All Companies</option>
                {companyOptions.map((company) => <option key={company.id} value={String(company.id)}>{company.company_name}</option>)}
              </select>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">KPIs</label>
              <div className="flex gap-2 mb-2">
                <button type="button" className={`px-3 py-1 text-xs rounded-full border transition-colors ${kpiSelectionMode === 'all' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-300'}`} onClick={() => setKpiSelectionMode('all')} disabled={calculating}>All KPIs ({rows.length})</button>
                <button type="button" className={`px-3 py-1 text-xs rounded-full border transition-colors ${kpiSelectionMode === 'selected' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-300'}`} onClick={() => setKpiSelectionMode('selected')} disabled={calculating}>Select KPIs</button>
              </div>

              {kpiSelectionMode === 'selected' && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3">
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                    <input className="w-full sm:flex-1 border rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900" placeholder="Search KPIs by id or name" value={kpiSearchQuery} onChange={(e) => setKpiSearchQuery(e.target.value)} />
                    <div className="flex items-center gap-2 text-xs">
                      <button type="button" className="text-emerald-700 dark:text-emerald-400" onClick={() => setSelectedKpiIds(new Set(visibleKpis.map((row) => Number(row.id)).filter(Boolean)))}>Select All</button>
                      <button type="button" className="text-gray-600 dark:text-gray-300" onClick={() => setSelectedKpiIds(new Set())}>Clear</button>
                      <span className="rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-1">{selectedKpiIds.size} selected</span>
                    </div>
                  </div>
                  <div className="mt-3 max-h-64 overflow-auto space-y-2">
                    {visibleKpis.map((row) => {
                      const id = Number(row.id);
                      const label = String(
                        pickValue(row, ['KPI_FORMULA', 'kpi_formula', 'kpi_name', 'KPI_Name', 'name']) || `KPI ${id}`,
                      );
                      return (
                        <label key={id || label} className="flex items-start gap-2 rounded-md px-2 py-2 hover:bg-gray-50 dark:hover:bg-gray-900 cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={selectedKpiIds.has(id)}
                            onChange={(e) => {
                              setSelectedKpiIds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(id);
                                else next.delete(id);
                                return next;
                              });
                            }}
                          />
                          <span className="text-sm text-gray-800 dark:text-gray-200">{`#${id} ${label}`}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-900/60 p-4">
              <div className="flex items-center justify-between gap-3 text-xs text-gray-700 dark:text-gray-200">
                <span className="font-medium">Companies completed</span>
                <span>{calculationProgress.companiesDone} / {calculationProgress.companiesTotal || calculationTargets.companiesTotal || 0}</span>
              </div>
              <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    !calculating && calculationProgress.companiesDone === calculationProgress.companiesTotal && calculationProgress.companiesTotal > 0
                      ? 'bg-green-600'
                      : 'bg-emerald-600'
                  }`}
                  style={{ width: `${companyProgressPct}%` }}
                />
              </div>

              <div className="mt-4 flex items-center justify-between gap-3 text-xs text-gray-700 dark:text-gray-200">
                <span className="font-medium">KPI calculations completed</span>
                <span>{calculationProgress.kpisDone} / {calculationProgress.kpisTotal || calculationTargets.kpiRunsTotal || 0}</span>
              </div>
              <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    !calculating && calculationProgress.kpisDone === calculationProgress.kpisTotal && calculationProgress.kpisTotal > 0
                      ? 'bg-green-600'
                      : 'bg-indigo-600'
                  }`}
                  style={{ width: `${kpiProgressPct}%` }}
                />
              </div>

              {calcCurrentCompany && (
                <div className="mt-3 flex items-center gap-2">
                  {calculating && (
                    <svg className="animate-spin h-4 w-4 text-emerald-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                  )}
                  <span className="text-xs text-gray-700 dark:text-gray-200 truncate">
                    {calculating ? `Processing: ${calcCurrentCompany}` : calcCurrentCompany}
                  </span>
                </div>
              )}

              {calcFailures.length > 0 && (
                <div className="mt-3 max-h-24 overflow-auto rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 p-2">
                  <div className="text-xs font-medium text-red-700 dark:text-red-300 mb-1">Failures ({calcFailures.length})</div>
                  {calcFailures.map((f, idx) => (
                    <div key={idx} className="text-[11px] text-red-600 dark:text-red-400 truncate">{f.company}: {f.error}</div>
                  ))}
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-600 dark:text-gray-300">
                <span>Selected KPIs: {calculationTargets.selectedKpisTotal}</span>
                <span>Eligible KPIs with SQL: {calculationTargets.eligibleKpisTotal}</span>
                <span>Selected companies: {calculationTargets.companiesTotal}</span>
              </div>
              {kpiSelectionMode === 'selected' && !eligibleCalculationRows.length ? (
                <div className="mt-2 text-[11px] text-amber-700 dark:text-amber-400">
                  Generate and approve SQL for the selected KPIs before running calculations.
                </div>
              ) : null}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700" onClick={() => { setShowCalculationModal(false); setCalcCurrentCompany(''); setCalcFailures([]); }} disabled={calculating}>Cancel</button>
              <button className="px-4 py-2 text-sm rounded-lg bg-emerald-700 text-white disabled:opacity-50" onClick={runCalculations} disabled={calculating || !canRunSelected}>{calculating ? 'Running...' : 'Run Calculations'}</button>
            </div>
          </div>
        </div>
      )}

      {showGenSqlSelectModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm px-4 py-8 overflow-auto">
          <div className="w-full max-w-xl rounded-2xl bg-white dark:bg-gray-950 border border-indigo-100 dark:border-gray-700 shadow-[0_20px_60px_rgba(0,0,0,0.15)] p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Generate SQL</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Choose which KPIs to generate SQL for.</p>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">KPIs</label>
              <div className="flex gap-2 mb-2">
                <button type="button" className={`px-3 py-1 text-xs rounded-full border transition-colors ${genSqlKpiMode === 'all' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-300'}`} onClick={() => setGenSqlKpiMode('all')}>All KPIs ({rows.length})</button>
                <button type="button" className={`px-3 py-1 text-xs rounded-full border transition-colors ${genSqlKpiMode === 'selected' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-300'}`} onClick={() => setGenSqlKpiMode('selected')}>Select KPIs</button>
              </div>

              {genSqlKpiMode === 'selected' && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3">
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                    <input className="w-full sm:flex-1 border rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900" placeholder="Search KPIs by id or name" value={genSqlSearchQuery} onChange={(e) => setGenSqlSearchQuery(e.target.value)} />
                    <div className="flex items-center gap-2 text-xs">
                      <button type="button" className="text-indigo-700 dark:text-indigo-400" onClick={() => setGenSqlSelectedIds(new Set(genSqlVisibleKpis.map((row) => Number(row.id)).filter(Boolean)))}>Select All</button>
                      <button type="button" className="text-gray-600 dark:text-gray-300" onClick={() => setGenSqlSelectedIds(new Set())}>Clear</button>
                      <span className="rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-1">{genSqlSelectedIds.size} selected</span>
                    </div>
                  </div>
                  <div className="mt-3 max-h-64 overflow-auto space-y-2">
                    {genSqlVisibleKpis.map((row) => {
                      const id = Number(row.id);
                      const label = String(
                        pickValue(row, ['KPI_FORMULA', 'kpi_formula', 'kpi_name', 'KPI_Name', 'name']) || `KPI ${id}`,
                      );
                      return (
                        <label key={id || label} className="flex items-start gap-2 rounded-md px-2 py-2 hover:bg-gray-50 dark:hover:bg-gray-900 cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={genSqlSelectedIds.has(id)}
                            onChange={(e) => {
                              setGenSqlSelectedIds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(id);
                                else next.delete(id);
                                return next;
                              });
                            }}
                          />
                          <span className="text-sm text-gray-800 dark:text-gray-200">{`#${id} ${label}`}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              {genSqlKpiMode === 'all' ? `${rows.length} KPIs will be processed` : `${genSqlSelectedIds.size} KPIs selected`}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700" onClick={() => setShowGenSqlSelectModal(false)}>Cancel</button>
              <button className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white disabled:opacity-50" onClick={startGenerateSql} disabled={genSqlKpiMode === 'selected' && genSqlSelectedIds.size === 0}>Generate</button>
            </div>
          </div>
        </div>
      )}

       {showAddKpiModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm px-4 py-8 overflow-auto">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-950 border border-emerald-100 dark:border-gray-700 shadow-[0_20px_60px_rgba(0,0,0,0.15)] p-5">
            <h3 className="text-base font-bold text-emerald-900 dark:text-white">Add New KPI</h3>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">Provide details for the new KPI row.</p>
            <div className="mt-4 space-y-2.5">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">KPI Name</label>
                <input
                  className="w-full rounded-xl border border-emerald-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 bg-white dark:bg-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-emerald-300 dark:hover:border-emerald-600 transition-colors"
                  value={newKpiName}
                  onChange={(e) => {
                    setNewKpiName(e.target.value);
                    if (addKpiApiMessage) setAddKpiApiMessage('');
                  }}
                  placeholder="Enter KPI Name"
                />
                {showErrorOnAddKpi && !newKpiName && (
                  <p className="mt-1 text-xs text-red-500">KPI Name is required.</p>
                )}
              </div>
               <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">KPI Description</label>
                <textarea
                  className="w-full rounded-xl border border-amber-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 bg-white dark:bg-gray-900 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 hover:border-amber-300 dark:hover:border-amber-600 transition-colors min-h-[96px] resize-y"
                  value={newKpiDescription}
                  onChange={(e) => {
                    setNewKpiDescription(e.target.value);
                    if (addKpiApiMessage) setAddKpiApiMessage('');
                  }}
                  placeholder="Enter KPI Description"
                />
                {showErrorOnAddKpi && !newKpiDescription && (
                  <p className="mt-1 text-xs text-red-500">KPI Description is required.</p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Function</label>
                <select
                  className="w-full rounded-xl border border-sky-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 hover:border-sky-300 dark:hover:border-sky-600 transition-colors"
                  value={newKpiFunction}
                  onChange={(e) => {
                    setNewKpiFunction(e.target.value);
                    if (addKpiApiMessage) setAddKpiApiMessage('');
                  }}
                >
                  <option value="">-- Select Function --</option>
                  {kpiFunctions.map((func) => (
                    <option key={func} value={func}>{func}</option>
                  ))}
                </select>
                {showErrorOnAddKpi && !newKpiFunction && (
                  <p className="mt-1 text-xs text-red-500">Function is required.</p>
                )}
              </div>
              {/* <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Source</label>
                <select
                  className="w-full rounded-xl border border-amber-200 dark:border-gray-700 px-3 py-2 text-sm bg-white dark:bg-gray-900 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 hover:border-amber-300 dark:hover:border-amber-600 transition-colors"
                  value={newKpiSource}
                  onChange={(e) => {
                    setNewKpiSource(e.target.value);
                    if (addKpiApiMessage) setAddKpiApiMessage('');
                  }}
                >
                  <option value="">-- Select Source --</option>
                  {kpiSources.map((source) => (
                    <option key={source} value={source}>{source}</option>
                  ))}
                </select>
                {showErrorOnAddKpi && !newKpiSource && (
                  <p className="mt-1 text-xs text-red-500">Source is required.</p>
                )}
              </div> */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">KPI Category</label>
                <select
                  className="w-full rounded-xl border border-teal-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 hover:border-teal-300 dark:hover:border-teal-600 transition-colors"
                  value={newKpiCategory}
                  onChange={(e) => {
                    setNewKpiCategory(e.target.value);
                    if (addKpiApiMessage) setAddKpiApiMessage('');
                  }}
                >
                  <option value="">-- Select KPI Category --</option>
                  {kpiCategories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                {showErrorOnAddKpi && !newKpiCategory && (
                  <p className="mt-1 text-xs text-red-500">KPI Category is required.</p>
                )}
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                className="rounded-xl border border-gray-300 dark:text-gray-100 dark:border-gray-700 px-4 py-2 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                onClick={() => setShowAddKpiModal(false)}
              >
                Cancel
              </button>
              <button
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 hover:shadow-md hover:shadow-emerald-300/40 dark:hover:shadow-none transition-all"
                onClick={submitNewKpi}
              >
                Add KPI
              </button>
            </div>
            {addKpiApiMessage && (
              <p className="mt-2 text-xs text-green-500 text-center">{addKpiApiMessage}</p>
            )}
            {addKpiLoading && (
              <div className="mt-3 flex justify-center">
                <svg className="animate-spin h-8 w-8 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
                </div>
            )}
          </div>
        </div>
      )}

      {showGenerateAllModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-950 shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Generating SQL for KPIs</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Processing each KPI individually and saving results.
            </p>

            <div className="mt-5">
              <div className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-200 mb-1">
                <span className="font-medium">Progress</span>
                <span>{generateAllProgress.done} / {generateAllProgress.total}</span>
              </div>
              <div className="h-4 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    generateAllProgress.done === generateAllProgress.total && !generatingAll
                      ? 'bg-green-600'
                      : 'bg-indigo-600 animate-pulse'
                  }`}
                  style={{ width: `${generateAllProgress.total ? Math.round((generateAllProgress.done / generateAllProgress.total) * 100) : 0}%` }}
                />
              </div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {Math.round((generateAllProgress.done / (generateAllProgress.total || 1)) * 100)}% complete
              </div>
            </div>

            {generateAllProgress.current && (
              <div className="mt-3 flex items-center gap-2">
                {generatingAll && !generateAllCancelled && (
                  <svg className="animate-spin h-4 w-4 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                )}
                <span className="text-sm text-gray-700 dark:text-gray-200 truncate">
                  {generatingAll ? `Generating: ${generateAllProgress.current}` : generateAllProgress.current}
                </span>
              </div>
            )}

            {generateAllProgress.failures.length > 0 && (
              <div className="mt-3 max-h-32 overflow-auto rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 p-2">
                <div className="text-xs font-medium text-red-700 dark:text-red-300 mb-1">Failures ({generateAllProgress.failures.length})</div>
                {generateAllProgress.failures.map((f, idx) => (
                  <div key={idx} className="text-xs text-red-600 dark:text-red-400 truncate">• {f.kpi}: {f.error}</div>
                ))}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              {generatingAll && !generateAllCancelled ? (
                <button
                  className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white"
                  onClick={() => { if (window.__generateAllCancel) window.__generateAllCancel(); }}
                >
                  Cancel
                </button>
              ) : (
                <button
                  className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white"
                  onClick={() => setShowGenerateAllModal(false)}
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showCalcResults && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 py-8 overflow-auto">
          <div
            className="w-full max-w-5xl rounded-xl shadow-lg p-5"
            style={{
              background: 'linear-gradient(180deg, var(--totals-bg-start), var(--totals-bg-end))',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold">Calculated KPI Results</h3>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Review saved KPI calculation values by company.</p>
              </div>
              <button
                className="px-3 py-2 text-sm rounded-lg font-medium"
                style={{
                  backgroundColor: 'var(--btn-primary)',
                  color: '#ffffff',
                  border: '1px solid var(--btn-primary-border)',
                }}
                onClick={() => setShowCalcResults(false)}
              >
                Close
              </button>
            </div>

            <div className="mt-4 max-w-sm">
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>Company</label>
              <select
                className="w-full rounded-lg px-3 py-2 text-sm"
                style={{
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                }}
                value={calcResultsCompanyId}
                onChange={(e) => setCalcResultsCompanyId(e.target.value)}
              >
                <option value="">Select a company</option>
                {companyOptions.map((company) => <option key={company.id} value={String(company.id)}>{company.company_name}</option>)}
              </select>
            </div>

            <div
              className="mt-4 overflow-auto rounded-lg"
              style={{
                border: '1px solid var(--border-color)',
                backgroundColor: 'var(--benchmark-container-bg)',
              }}
            >
              {loadingCalcResults ? (
                <div className="p-8 text-sm" style={{ color: 'var(--text-secondary)' }}>Loading results...</div>
              ) : !calcResultsData.length ? (
                <div className="p-8 text-sm" style={{ color: 'var(--text-secondary)' }}>No calculated values found for the selected company.</div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                    <tr>
                      {Object.keys(calcResultsData[0]).map((col) => <th key={col} className="p-2 text-left whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>{toHeader(col)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {calcResultsData.map((row, idx) => (
                      <tr key={idx} className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                        {Object.keys(calcResultsData[0]).map((col) => <td key={col} className="p-2" style={{ color: 'var(--text-primary)' }}>{formatCalcCellValue(row[col], col)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
